/**
 * Category tabs for the product showcase section.
 *
 * All panels are rendered server-side; this element only switches which one is
 * visible. Hidden panels keep their lazy images unloaded until they are shown,
 * and each panel's <logo-row> re-measures itself through its ResizeObserver.
 *
 * Follows the WAI-ARIA tabs pattern: arrow keys, Home and End move between
 * tabs, and only the selected tab is in the tab order.
 */
class ShowcaseTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    if (this.tabs.length === 0) return;

    this.onClick = (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab) this.select(tab);
    };
    this.onKeydown = (event) => this.handleKeydown(event);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
  }

  /** @param {HTMLElement} tab */
  select(tab, focus = false) {
    for (const t of this.tabs) {
      const aktiv = t === tab;
      t.setAttribute('aria-selected', String(aktiv));
      t.tabIndex = aktiv ? 0 : -1;

      const panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !aktiv;
    }

    if (focus) tab.focus();

    // Keep the active tab visible when the tab row scrolls on small screens.
    tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    const index = this.tabs.indexOf(/** @type {HTMLElement} */ (event.target));
    if (index === -1) return;

    let ziel;
    switch (event.key) {
      case 'ArrowRight':
        ziel = this.tabs[(index + 1) % this.tabs.length];
        break;
      case 'ArrowLeft':
        ziel = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        break;
      case 'Home':
        ziel = this.tabs[0];
        break;
      case 'End':
        ziel = this.tabs[this.tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(ziel, true);
  }
}

if (!customElements.get('showcase-tabs')) {
  customElements.define('showcase-tabs', ShowcaseTabs);
}
